import { api } from '../../lib/api';

/**
 * Mastering, as the page sees it. The engine is IrateTech's and every
 * number that reaches it is composed on the server (`Domain\Mastering\Chain`);
 * this client sends a tile, a slider and three knobs and gets renders back.
 */

export interface Preset {
  slug: string;
  name: string;
  blurb: string;
  icon: string;
  target_lufs: number;
  version: number;
}

export interface UserEq {
  low_db: number;
  mid_db: number;
  high_db: number;
}

/** The engine's chain object, as the server composes it. */
export interface Chain {
  eq_curve: [number, number][];
  user_eq: UserEq;
  multiband: {
    splits_hz: number[];
    bands: { threshold_db: number; ratio: number; attack_ms: number; release_ms: number; makeup_db: number }[];
  };
  saturation: { amount: number; drive: number; blend: number };
  width: { side_gain_db: number; bass_mono_hz: number };
  loudness: { target_lufs: number; true_peak_dbtp: number; lra: number };
  bypass: { multiband: boolean; saturation: boolean; width: boolean };
}

export interface Measured {
  input?: { integrated_lufs: number; true_peak_dbtp: number; loudness_range_lu: number; correlation: number; clipped: boolean };
  output?: { integrated_lufs: number; true_peak_dbtp: number; loudness_range_lu: number; correlation: number };
  render?: { loudness_mode: 'linear' | 'dynamic'; gain_db: number | null; already_mastered: boolean; mono_input: boolean; side_gain_halved: boolean };
}

export interface Render {
  id: number;
  track_id: number;
  preset: string;
  intensity: number;
  user_eq: UserEq;
  with_reference: boolean;
  chain_version: number;
  status: 'queued' | 'rendering' | 'done' | 'failed';
  error: { code: string; message: string } | null;
  /** The 320k preview, a seven-day link from the engine. */
  preview_url: string | null;
  has_master: boolean;
  kept: boolean;
  measured: Measured | null;
  waveform: { points: number; duration_sec: number; peaks: number[] } | null;
  already_mastered: boolean;
  cached: boolean;
  expires_at: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface WorkbenchTrack {
  id: number;
  title: string;
  artwork_small: string | null;
  duration_ms: number | null;
  status: string;
  ready: boolean;
  has_tone: boolean;
  reference: { label: string; state: 'analysing' | 'pending' | 'ready' } | null;
  latest: Render | null;
}

export interface Workbench {
  enabled: boolean;
  presets: Preset[];
  tracks: WorkbenchTrack[];
}

export const mastering = {
  workbench: () => api.get<Workbench>('/mastering'),

  render: (trackId: number, input: { preset: string; intensity: number; user_eq: UserEq; use_reference: boolean }) =>
    api.post<Render>(`/mastering/tracks/${trackId}/render`, input),

  /** The chain a render would send, for the browser preview. Nothing reaches the engine. */
  compose: (trackId: number, input: { preset: string; intensity: number; user_eq: UserEq; use_reference: boolean }) =>
    api.post<{ chain: Chain; input_lufs: number | null }>(`/mastering/tracks/${trackId}/compose`, input),

  renders: (trackId: number) => api.get<{ items: Render[] }>(`/mastering/tracks/${trackId}/renders`),

  render_: (renderId: number) => api.get<Render>(`/mastering/renders/${renderId}`),

  /** A short link to the upload, for the Original side. Fifteen minutes. */
  original: (trackId: number) => api.get<{ url: string; expires_in: number }>(`/mastering/tracks/${trackId}/original`),

  /** Copies the 24-bit master into our bucket the first time; a fresh link every time. */
  keep: (renderId: number) => api.post<{ url: string }>(`/mastering/renders/${renderId}/keep`),

  referenceInit: (trackId: number, contentType: string, sizeBytes: number) =>
    api.post<{ url: string; key: string; headers: Record<string, string> }>(`/mastering/tracks/${trackId}/reference/init`, {
      content_type: contentType,
      size_bytes: sizeBytes,
    }),

  referenceComplete: (trackId: number, key: string, label: string) =>
    api.post<{ job_id: string }>(`/mastering/tracks/${trackId}/reference/complete`, { key, label }),

  referenceClear: (trackId: number) => api.delete(`/mastering/tracks/${trackId}/reference`),
};

/** Where the slider snaps: the engine's intensity scalar, 0 to 2. */
export const INTENSITY_STOPS: { value: number; label: string }[] = [
  { value: 0, label: 'Light' },
  { value: 0.5, label: '' },
  { value: 1, label: 'Normal' },
  { value: 1.5, label: '' },
  { value: 2, label: 'Heavy' },
];
